import { memo } from 'react';
import { css } from '@emotion/react';
import { Icon, Typography } from '@uniquegood/realworld-studio-design';
import { KeyedMutator } from 'swr';

import {
  SidePanelContainerStyle,
  SidePanelHeaderStyle,
  SidePanelBodyStyle,
  SidePanelCloseButton
} from '../styles';
import { SIDE_PANEL_WIDTH } from '../constants';
import type { SidePanelMode } from '../types';
import SidePanelMissionEdit from './SidePanelMissionEdit';
import SidePanelQuestEdit from './SidePanelQuestEdit';

interface FlowSidePanelProps {
  mode: SidePanelMode;
  onClose: () => void;
  mutate: KeyedMutator<Mission[]>;
  onDeleteMission: (missionId: number) => void;
}

function getPanelTitle(mode: SidePanelMode): string {
  switch (mode.type) {
    case 'mission':
      return '미션 편집';
    case 'quest':
      return '퀘스트 편집';
    case 'createQuest':
      return '새 퀘스트';
    default:
      return '';
  }
}

function FlowSidePanel({ mode, onClose, mutate, onDeleteMission }: FlowSidePanelProps) {
  const isOpen = mode.type !== 'none';

  return (
    <div css={[SidePanelContainerStyle, SlideTransition, isOpen ? SlideIn : SlideOut]}>
      <header css={SidePanelHeaderStyle}>
        <Typography as="h3" type="button">
          {getPanelTitle(mode)}
        </Typography>
        <button type="button" css={SidePanelCloseButton} onClick={onClose}>
          <Icon icon="times_light" size="18px" />
        </button>
      </header>
      <div css={SidePanelBodyStyle}>
        {mode.type === 'mission' && (
          <SidePanelMissionEdit
            key={mode.missionId}
            missionId={mode.missionId}
            mutate={mutate}
            onDelete={() => onDeleteMission(mode.missionId)}
          />
        )}
        {(mode.type === 'quest' || mode.type === 'createQuest') && (
          <SidePanelQuestEdit
            key={mode.type === 'quest' ? mode.questId : `new-${mode.missionId}`}
            missionId={mode.missionId}
            questId={mode.type === 'quest' ? mode.questId : undefined}
            mutate={mutate}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

export default memo(FlowSidePanel);

const SlideTransition = css`
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
`;

const SlideIn = css`
  transform: translateX(0);
  pointer-events: auto;
`;

const SlideOut = css`
  transform: translateX(${SIDE_PANEL_WIDTH}px);
  pointer-events: none;
`;
